import { useEffect, useState } from "react";
import {
  adminGetSidebarCategories,
  adminCreateSidebarCategory,
  adminUpdateSidebarCategory,
  adminDeleteSidebarCategory,
  adminCreateSidebarLink,
  adminUpdateSidebarLink,
  adminDeleteSidebarLink,
} from "../services/api";

const CONTENT_TYPES = [
  { value: "classes",   label: "Clases (automático)"    },
  { value: "articles",  label: "Artículos (automático)" },
  { value: "resources", label: "Recursos (automático)"  },
  { value: "links",     label: "Links externos manuales"},
];

const TYPE_COLORS = {
  classes:   { bg:"var(--teal-light)",  color:"var(--teal-dark)"  },
  articles:  { bg:"#eaf3fb",            color:"#2980b9"            },
  resources: { bg:"#f0ebe8",            color:"#795548"            },
  links:     { bg:"#fdf3dc",            color:"#a07800"            },
};

// ── Modal de confirmación ─────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 320,
          background: "var(--white)",
          border: "0.5px solid var(--border)",
          borderRadius: "var(--card-radius)",
          padding: "1.5rem",
          display: "flex", flexDirection: "column", gap: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "#fdecea",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 5v3M8 10.5h.01M2 14h12L8 2 2 14z"
                stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </div>
          <p style={{
            fontFamily: "var(--font-display)", fontWeight: 700,
            fontSize: "0.85rem", color: "var(--text-dark)", margin: 0,
          }}>
            Confirmar eliminación
          </p>
        </div>

        <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
          {message}
        </p>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
          <button className="btn-outline" style={{ fontSize: "0.75rem" }} onClick={onCancel}>
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            style={{
              fontSize: "0.75rem", padding: "5px 14px", borderRadius: 6, cursor: "pointer",
              background: "#fdecea", color: "#c0392b",
              border: "0.5px solid #e8a0a0",
              fontFamily: "var(--font-display)", fontWeight: 700,
            }}
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Form inline de categoría ──────────────────────────────────────
function CategoryForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name:         initial?.name         || "",
    content_type: initial?.content_type || "classes",
    order_index:  initial?.order_index  ?? 0,
    is_visible:   initial?.is_visible   ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} style={{ background:"var(--teal-light)", borderRadius:8, padding:"1rem", marginBottom:".8rem", display:"flex", flexDirection:"column", gap:".6rem" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".6rem" }}>
        <div className="form-group" style={{ margin:0 }}>
          <label>Nombre de la sección</label>
          <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ej: Clases recientes" required />
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label>Tipo de contenido</label>
          <select value={form.content_type} onChange={(e) => setForm({...form, content_type: e.target.value})}>
            {CONTENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display:"flex", gap:".6rem", alignItems:"center", flexWrap:"wrap" }}>
        <div className="form-group" style={{ margin:0, width:100 }}>
          <label>Orden</label>
          <input type="number" min={0} value={form.order_index} onChange={(e) => setForm({...form, order_index: parseInt(e.target.value)||0})} />
        </div>
        <label style={{ display:"flex", alignItems:"center", gap:".4rem", fontSize:"0.82rem", marginTop:"1.2rem" }}>
          <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({...form, is_visible: e.target.checked})} />
          Visible
        </label>
        <div style={{ marginLeft:"auto", display:"flex", gap:".5rem", marginTop:"1.2rem" }}>
          <button type="button" onClick={onCancel} className="btn-outline" style={{ fontSize:"0.75rem" }}>Cancelar</button>
          <button type="submit" className="btn-gold" style={{ fontSize:"0.75rem" }}>
            {initial ? "Guardar cambios" : "Crear sección"}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Form inline de link externo ───────────────────────────────────
function LinkForm({ categoryId, initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    label:       initial?.label       || "",
    url:         initial?.url         || "https://",
    order_index: initial?.order_index ?? 0,
    is_visible:  initial?.is_visible  ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.url.trim()) return;
    onSave({ ...form, category_id: categoryId });
  };

  return (
    <form onSubmit={handleSubmit} style={{ background:"#f7fafa", border:"0.5px dashed var(--border)", borderRadius:6, padding:".8rem", display:"flex", flexDirection:"column", gap:".5rem", marginTop:".4rem" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".5rem" }}>
        <div className="form-group" style={{ margin:0 }}>
          <label>Texto del link</label>
          <input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder="Ej: Johns Hopkins" required />
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label>URL</label>
          <input type="url" value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} required />
        </div>
      </div>
      <div style={{ display:"flex", gap:".5rem", justifyContent:"flex-end" }}>
        <button type="button" onClick={onCancel} className="btn-outline" style={{ fontSize:"0.72rem", padding:"4px 12px" }}>Cancelar</button>
        <button type="submit" className="btn-primary" style={{ fontSize:"0.72rem", padding:"4px 12px" }}>
          {initial ? "Guardar" : "Agregar link"}
        </button>
      </div>
    </form>
  );
}

// ── Componente principal ──────────────────────────────────────────
export default function SidebarManager() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [newCatForm, setNewCatForm] = useState(false);
  const [editCat,    setEditCat]    = useState(null);
  const [newLinkFor, setNewLinkFor] = useState(null);
  const [editLink,   setEditLink]   = useState(null);
  const [confirm,    setConfirm]    = useState(null); // { message, onConfirm }

  const load = async () => {
    setLoading(true);
    try { setCategories(await adminGetSidebarCategories()); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Categorías
  const handleCreateCat = async (form) => {
    await adminCreateSidebarCategory(form);
    setNewCatForm(false);
    load();
  };
  const handleUpdateCat = async (id, form) => {
    await adminUpdateSidebarCategory(id, form);
    setEditCat(null);
    load();
  };
  const handleDeleteCat = (id) => {
    setConfirm({
      message: "¿Eliminar esta sección del sidebar? También se eliminarán sus links externos.",
      onConfirm: async () => {
        setConfirm(null);
        await adminDeleteSidebarCategory(id);
        load();
      },
    });
  };

  // Links
  const handleCreateLink = async (form) => {
    await adminCreateSidebarLink(form);
    setNewLinkFor(null);
    load();
  };
  const handleUpdateLink = async (id, form) => {
    await adminUpdateSidebarLink(id, form);
    setEditLink(null);
    load();
  };
  const handleDeleteLink = (id) => {
    setConfirm({
      message: "¿Eliminar este link?",
      onConfirm: async () => {
        setConfirm(null);
        await adminDeleteSidebarLink(id);
        load();
      },
    });
  };

  if (loading) return <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando sidebar...</p>;

  return (
    <div>
      <div className="admin-toolbar">
        <h2>Secciones del sidebar</h2>
        {!newCatForm && (
          <button className="btn-primary" onClick={() => setNewCatForm(true)}>+ Nueva sección</button>
        )}
      </div>

      {newCatForm && (
        <CategoryForm onSave={handleCreateCat} onCancel={() => setNewCatForm(false)} />
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
        {categories.length === 0 && (
          <p className="admin-empty">No hay secciones en el sidebar. Crea una arriba.</p>
        )}

        {categories.map((cat) => {
          const tc = TYPE_COLORS[cat.content_type] || TYPE_COLORS.links;
          const isEditing = editCat === cat.id;

          return (
            <div key={cat.id} style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:"var(--card-radius)", overflow:"hidden" }}>
              {/* Header de la categoría */}
              {isEditing ? (
                <div style={{ padding:"1rem" }}>
                  <CategoryForm
                    initial={cat}
                    onSave={(form) => handleUpdateCat(cat.id, form)}
                    onCancel={() => setEditCat(null)}
                  />
                </div>
              ) : (
                <div style={{ background:"linear-gradient(135deg,var(--teal-dark),var(--teal))", padding:".6rem 1rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:".5rem" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:".6rem", flexWrap:"wrap" }}>
                    <span style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.78rem", color:"#fff", letterSpacing:.8, textTransform:"uppercase" }}>
                      {cat.name}
                    </span>
                    <span style={{ fontSize:"0.65rem", fontFamily:"var(--font-display)", fontWeight:700, background:tc.bg, color:tc.color, padding:"2px 8px", borderRadius:20 }}>
                      {CONTENT_TYPES.find((t) => t.value === cat.content_type)?.label}
                    </span>
                    {!cat.is_visible && (
                      <span style={{ fontSize:"0.65rem", fontFamily:"var(--font-display)", fontWeight:700, background:"rgba(0,0,0,.2)", color:"#fff", padding:"2px 8px", borderRadius:20 }}>
                        Oculto
                      </span>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:".4rem", flexShrink:0 }}>
                    <button
                      onClick={() => setEditCat(cat.id)}
                      style={{ background:"rgba(255,255,255,.2)", border:"none", color:"#fff", fontSize:"0.72rem", fontFamily:"var(--font-display)", fontWeight:600, padding:"3px 10px", borderRadius:4, cursor:"pointer" }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteCat(cat.id)}
                      style={{ background:"rgba(192,57,43,.7)", border:"none", color:"#fff", fontSize:"0.72rem", fontFamily:"var(--font-display)", fontWeight:600, padding:"3px 10px", borderRadius:4, cursor:"pointer" }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}

              {/* Contenido: info o links */}
              <div style={{ padding:".8rem 1rem" }}>
                {cat.content_type !== "links" ? (
                  <p style={{ fontSize:"0.78rem", color:"var(--text-muted)", fontStyle:"italic" }}>
                    Mostrará automáticamente los últimos 6 {cat.content_type === "classes" ? "clases" : cat.content_type === "articles" ? "artículos" : "recursos"} publicados.
                  </p>
                ) : (
                  <>
                    {/* Lista de links externos */}
                    <div style={{ display:"flex", flexDirection:"column", gap:".4rem" }}>
                      {cat.links?.length === 0 && (
                        <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>Sin links. Agrega uno abajo.</p>
                      )}
                      {cat.links?.map((link) => (
                        <div key={link.id}>
                          {editLink === link.id ? (
                            <LinkForm
                              categoryId={cat.id}
                              initial={link}
                              onSave={(form) => handleUpdateLink(link.id, form)}
                              onCancel={() => setEditLink(null)}
                            />
                          ) : (
                            <div style={{ display:"flex", alignItems:"center", gap:".5rem", padding:".4rem .6rem", background:"var(--off-white)", borderRadius:6 }}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <span style={{ fontSize:"0.82rem", color:"var(--teal)", fontWeight:500 }}>{link.label}</span>
                                <span style={{ fontSize:"0.7rem", color:"var(--text-light)", marginLeft:".5rem" }}>{link.url}</span>
                              </div>
                              <button onClick={() => setEditLink(link.id)} style={{ background:"none", border:"0.5px solid var(--border)", fontSize:"0.68rem", fontFamily:"var(--font-display)", fontWeight:600, padding:"2px 8px", borderRadius:4, cursor:"pointer", color:"var(--text-muted)", whiteSpace:"nowrap" }}>Editar</button>
                              <button onClick={() => handleDeleteLink(link.id)} style={{ background:"none", border:"0.5px solid #e8a0a0", fontSize:"0.68rem", fontFamily:"var(--font-display)", fontWeight:600, padding:"2px 8px", borderRadius:4, cursor:"pointer", color:"#c0392b", whiteSpace:"nowrap" }}>✕</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Botón / form para nuevo link */}
                    {newLinkFor === cat.id ? (
                      <LinkForm
                        categoryId={cat.id}
                        onSave={handleCreateLink}
                        onCancel={() => setNewLinkFor(null)}
                      />
                    ) : (
                      <button
                        onClick={() => setNewLinkFor(cat.id)}
                        style={{ marginTop:".5rem", background:"none", border:"1.5px dashed var(--border)", borderRadius:6, width:"100%", padding:".4rem", fontSize:"0.75rem", fontFamily:"var(--font-display)", fontWeight:600, color:"var(--teal)", cursor:"pointer", transition:"border-color .15s" }}
                        onMouseEnter={(e) => e.target.style.borderColor="var(--teal)"}
                        onMouseLeave={(e) => e.target.style.borderColor="var(--border)"}
                      >
                        + Agregar link
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de confirmación */}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}